import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faEraser, faXmark, faPenNib, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { PartnerService } from '../../../core/services/partner.service';
import { PopupInfoService } from '../../services/popup-info.service';

@Component({
  selector: 'app-signature-pad-modal',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule],
  templateUrl: './signature-pad-modal.html',
  styleUrls: ['./signature-pad-modal.scss'],
})
export class SignaturePadModal implements AfterViewInit {
  @Input({ required: true }) contractNumber!: number;
  @Input() documentName: string | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() signed = new EventEmitter<void>();

  @ViewChild('signatureCanvas') canvasRef!: ElementRef<HTMLCanvasElement>;

  private readonly partnerService = inject(PartnerService);
  private readonly popupInfoService = inject(PopupInfoService);

  private ctx!: CanvasRenderingContext2D;
  private drawing = false;
  private lastX = 0;
  private lastY = 0;

  hasSignature = false;
  isSubmitting = false;

  readonly faEraser = faEraser;
  readonly faXmark = faXmark;
  readonly faPenNib = faPenNib;
  readonly faSpinner = faSpinner;

  ngAfterViewInit(): void {
    const context = this.canvasRef.nativeElement.getContext('2d');
    if (!context) return;
    this.ctx = context;
    this.ctx.lineWidth = 2.5;
    this.ctx.lineCap = 'round';
    this.ctx.strokeStyle = '#111827';
  }

  private getPos(event: MouseEvent | TouchEvent): { x: number; y: number } {
    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const point = 'touches' in event ? event.touches[0] ?? event.changedTouches[0] : event;
    return {
      x: ((point.clientX - rect.left) / rect.width) * canvas.width,
      y: ((point.clientY - rect.top) / rect.height) * canvas.height,
    };
  }

  startDrawing(event: MouseEvent | TouchEvent): void {
    event.preventDefault();
    this.drawing = true;
    const { x, y } = this.getPos(event);
    this.lastX = x;
    this.lastY = y;
  }

  draw(event: MouseEvent | TouchEvent): void {
    if (!this.drawing) return;
    event.preventDefault();
    const { x, y } = this.getPos(event);
    this.ctx.beginPath();
    this.ctx.moveTo(this.lastX, this.lastY);
    this.ctx.lineTo(x, y);
    this.ctx.stroke();
    this.lastX = x;
    this.lastY = y;
    this.hasSignature = true;
  }

  stopDrawing(): void {
    this.drawing = false;
  }

  clear(): void {
    const canvas = this.canvasRef.nativeElement;
    this.ctx.clearRect(0, 0, canvas.width, canvas.height);
    this.hasSignature = false;
  }

  confirmSignature(): void {
    if (!this.hasSignature || this.isSubmitting) return;

    const signatureImage = this.canvasRef.nativeElement.toDataURL('image/png');

    this.isSubmitting = true;
    this.partnerService.signContract(this.contractNumber, signatureImage).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.popupInfoService.showSuccess('Contrato firmado correctamente.');
        this.signed.emit();
      },
      error: () => {
        this.isSubmitting = false;
        this.popupInfoService.showError('No se ha podido firmar el contrato. Inténtalo de nuevo.');
      },
    });
  }

  onClose(): void {
    if (this.isSubmitting) return;
    this.close.emit();
  }
}
